import {timezoneSafeTimestamp} from '../core/time.js';
import {
  createSmokerSnapshot,
  normaliseSmokerSnapshot,
  smokerSnapshotToJson
} from './equipment-snapshot.js';

export const SmokingSessionStatus=Object.freeze({
  DRAFT:'draft',
  PLANNED:'planned',
  PREPARATION:'preparation',
  READY:'ready',
  SMOKING:'smoking',
  RESTING:'resting',
  COMPLETED:'completed',
  CANCELLED:'cancelled'
});

const STATUSES=new Set(Object.values(SmokingSessionStatus));
const TERMINAL=new Set([SmokingSessionStatus.COMPLETED,SmokingSessionStatus.CANCELLED]);
const EQUIPMENT_EDITABLE=new Set([
  SmokingSessionStatus.DRAFT,
  SmokingSessionStatus.PLANNED,
  SmokingSessionStatus.PREPARATION,
  SmokingSessionStatus.READY
]);

const TRANSITIONS=Object.freeze({
  draft:new Set(['planned','cancelled']),
  planned:new Set(['preparation','ready','smoking','cancelled']),
  preparation:new Set(['ready','smoking','cancelled']),
  ready:new Set(['smoking','cancelled']),
  smoking:new Set(['resting','completed','cancelled']),
  resting:new Set(['completed','cancelled']),
  completed:new Set(),
  cancelled:new Set()
});

function required(value,label,maxLength=128){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>maxLength)throw new TypeError(label+' is too long');
  return text;
}

function optionalId(value,label){
  if(value==null||value==='')return null;
  return required(value,label,128);
}

function optionalText(value,maxLength){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(!text)return null;
  if(text.length>maxLength)throw new TypeError('Text value is too long');
  return text;
}

function optionalPositiveInteger(value,label){
  if(value==null||value==='')return null;
  const number=Number(value);
  if(!Number.isInteger(number)||number<1)throw new TypeError(label+' must be a positive integer');
  return number;
}

function optionalJsonObject(value,label,maxLength=8000){
  if(value==null)return null;
  if(typeof value!=='object'||Array.isArray(value))throw new TypeError(label+' must be an object');
  const json=JSON.stringify(value);
  if(json.length>maxLength)throw new TypeError(label+' is too large');
  return Object.freeze(JSON.parse(json));
}

function status(value){
  const next=String(value||'').trim().toLowerCase();
  if(!STATUSES.has(next))throw new TypeError('Invalid smoking session status');
  return next;
}

function timestamp(value,label){
  if(value==null||value==='')return null;
  return timezoneSafeTimestamp(value,label);
}

function idOrGenerated(value){
  const id=String(value||'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

function assertChronology(value){
  const plannedStart=value.plannedStartAt?Date.parse(value.plannedStartAt):null;
  const plannedEnd=value.plannedEndAt?Date.parse(value.plannedEndAt):null;
  const actualStart=value.actualStartAt?Date.parse(value.actualStartAt):null;
  const actualEnd=value.actualEndAt?Date.parse(value.actualEndAt):null;

  if(plannedStart!=null&&plannedEnd!=null&&plannedEnd<plannedStart){
    throw new TypeError('plannedEndAt cannot be before plannedStartAt');
  }
  if(actualStart!=null&&actualEnd!=null&&actualEnd<actualStart){
    throw new TypeError('actualEndAt cannot be before actualStartAt');
  }
  if(['smoking','resting','completed'].includes(value.status)&&!value.actualStartAt){
    throw new TypeError('actualStartAt is required once smoking starts');
  }
  if(value.status==='completed'&&!value.actualEndAt){
    throw new TypeError('actualEndAt is required for a completed session');
  }
}

function assertEquipment(value){
  if(value.smokerId&&!value.smokerSnapshot){
    throw new TypeError('smokerSnapshot is required when smokerId is set');
  }
  if(value.smokerId&&value.smokerSnapshot&&value.smokerSnapshot.smokerId!==value.smokerId){
    throw new TypeError('smokerSnapshot must match smokerId');
  }
}

function assertLiveState(value){
  if(Boolean(value.activeStageId)!==Boolean(value.activeStageStartedAt)){
    throw new TypeError('activeStageId and activeStageStartedAt must be set together');
  }
  if(value.status===SmokingSessionStatus.SMOKING&&!value.activeStageId){
    throw new TypeError('activeStageId is required while smoking');
  }
  if(value.activeStageStartedAt&&value.actualStartAt
    &&Date.parse(value.activeStageStartedAt)<Date.parse(value.actualStartAt)){
    throw new TypeError('activeStageStartedAt cannot be before actualStartAt');
  }
}

export function createSmokingSession({
  id,
  userId,
  status:sessionStatus='draft',
  title=null,
  smokerId=null,
  smokerSnapshot=null,
  recipeId=null,
  recipeVersion=null,
  recipeSourceSnapshot=null,
  recipeUseRequestId=null,
  plannerScheduleSnapshot=null,
  sourceSessionId=null,
  plannedStartAt=null,
  plannedEndAt=null,
  actualStartAt=null,
  actualEndAt=null,
  activeStageId=null,
  activeStageStartedAt=null,
  stageMutationBatchId=null,
  statusChangedAt=null,
  deletedAt=null
}={}){
  const value={
    id:idOrGenerated(id),
    userId:required(userId,'userId'),
    status:status(sessionStatus),
    title:optionalText(title,160),
    smokerId:optionalId(smokerId,'smokerId'),
    smokerSnapshot:smokerSnapshot==null?null:normaliseSmokerSnapshot(smokerSnapshot),
    recipeId:optionalId(recipeId,'recipeId'),
    recipeVersion:optionalPositiveInteger(recipeVersion,'recipeVersion'),
    recipeSourceSnapshot:optionalJsonObject(recipeSourceSnapshot,'recipeSourceSnapshot'),
    recipeUseRequestId:optionalId(recipeUseRequestId,'recipeUseRequestId'),
    plannerScheduleSnapshot:optionalJsonObject(plannerScheduleSnapshot,'plannerScheduleSnapshot',64000),
    sourceSessionId:optionalId(sourceSessionId,'sourceSessionId'),
    plannedStartAt:timestamp(plannedStartAt,'plannedStartAt'),
    plannedEndAt:timestamp(plannedEndAt,'plannedEndAt'),
    actualStartAt:timestamp(actualStartAt,'actualStartAt'),
    actualEndAt:timestamp(actualEndAt,'actualEndAt'),
    activeStageId:optionalId(activeStageId,'activeStageId'),
    activeStageStartedAt:timestamp(activeStageStartedAt,'activeStageStartedAt'),
    stageMutationBatchId:optionalId(stageMutationBatchId,'stageMutationBatchId'),
    statusChangedAt:timestamp(statusChangedAt,'statusChangedAt'),
    deletedAt:timestamp(deletedAt,'deletedAt')
  };
  if(value.sourceSessionId===value.id)throw new TypeError('sourceSessionId cannot reference the same session');
  assertEquipment(value);
  assertChronology(value);
  assertLiveState(value);
  return Object.freeze(value);
}

export function canTransitionSmokingSession(from,to){
  const current=status(from);
  const next=status(to);
  return current===next||TRANSITIONS[current].has(next);
}

export function startSmokingSession(session,{
  stageId,
  at=new Date().toISOString()
}={}){
  const current=createSmokingSession(session);
  if(current.status===SmokingSessionStatus.SMOKING||current.actualStartAt){
    throw new TypeError('SMOKING_SESSION_ALREADY_STARTED');
  }
  if(![
    SmokingSessionStatus.PLANNED,
    SmokingSessionStatus.PREPARATION,
    SmokingSessionStatus.READY
  ].includes(current.status)){
    throw new TypeError('SMOKING_SESSION_NOT_STARTABLE');
  }

  const when=timestamp(at,'at');
  const activeStageId=required(stageId,'stageId');
  return createSmokingSession({
    ...current,
    status:SmokingSessionStatus.SMOKING,
    actualStartAt:when,
    activeStageId,
    activeStageStartedAt:when,
    statusChangedAt:when,
    id:current.id,
    userId:current.userId
  });
}

export function changeActiveSmokingStage(session,{
  stageId,
  at=new Date().toISOString(),
  mutationBatchId=undefined
}={}){
  const current=createSmokingSession(session);
  if(current.status!==SmokingSessionStatus.SMOKING){
    throw new TypeError('LIVE_STAGE_TRANSITION_REQUIRES_SMOKING');
  }
  const when=timestamp(at,'at');
  return createSmokingSession({
    ...current,
    activeStageId:required(stageId,'stageId'),
    activeStageStartedAt:when,
    stageMutationBatchId:mutationBatchId===undefined
      ?current.stageMutationBatchId
      :optionalId(mutationBatchId,'mutationBatchId'),
    id:current.id,
    userId:current.userId
  });
}

export function transitionSmokingSession(session,nextStatus,{at=new Date().toISOString()}={}){
  const current=createSmokingSession(session);
  const next=status(nextStatus);
  if(current.status===next)return current;
  if(next===SmokingSessionStatus.SMOKING){
    throw new TypeError('Use startSmokingSession to enter smoking state');
  }
  if(!canTransitionSmokingSession(current.status,next)){
    throw new TypeError('Invalid smoking session transition: '+current.status+' -> '+next);
  }

  const when=timestamp(at,'at');
  const changes={status:next,statusChangedAt:when};

  if(current.status===SmokingSessionStatus.SMOKING){
    changes.activeStageId=null;
    changes.activeStageStartedAt=null;
  }
  if(next===SmokingSessionStatus.COMPLETED){
    changes.actualEndAt=current.actualEndAt||when;
  }
  if(next===SmokingSessionStatus.CANCELLED&&current.actualStartAt&&!current.actualEndAt){
    changes.actualEndAt=when;
  }

  return createSmokingSession({...current,...changes,id:current.id,userId:current.userId});
}

export function updateSmokingSession(session,changes={}){
  const current=createSmokingSession(session);
  if('status' in changes&&String(changes.status||'').trim().toLowerCase()!==current.status){
    throw new TypeError('Use transitionSmokingSession to change status');
  }
  if('smokerId' in changes||'smokerSnapshot' in changes){
    throw new TypeError('Use attachCurrentSmokerSnapshot or clearSessionSmoker to change equipment');
  }
  if('activeStageId' in changes||'activeStageStartedAt' in changes||'stageMutationBatchId' in changes){
    throw new TypeError('Use Live Smoking transitions to change active stage');
  }
  for(const key of ['recipeId','recipeVersion','recipeSourceSnapshot','recipeUseRequestId']){
    if(key in changes&&JSON.stringify(changes[key]??null)!==JSON.stringify(current[key]??null)){
      throw new TypeError('Recipe attribution is immutable after session creation');
    }
  }
  return createSmokingSession({...current,...changes,id:current.id,userId:current.userId,status:current.status});
}

export function attachCurrentSmokerSnapshot(session,{
  smoker,
  capabilities,
  capturedAt=new Date().toISOString()
}={}){
  const current=createSmokingSession(session);
  if(!EQUIPMENT_EDITABLE.has(current.status)){
    throw new TypeError('Session equipment is locked after live smoking starts');
  }
  const snapshot=createSmokerSnapshot({smoker,capabilities,capturedAt});
  return createSmokingSession({
    ...current,
    smokerId:snapshot.smokerId,
    smokerSnapshot:snapshot,
    id:current.id,
    userId:current.userId,
    status:current.status
  });
}

export function clearSessionSmoker(session){
  const current=createSmokingSession(session);
  if(!EQUIPMENT_EDITABLE.has(current.status)){
    throw new TypeError('Session equipment is locked after live smoking starts');
  }
  return createSmokingSession({
    ...current,
    smokerId:null,
    smokerSnapshot:null,
    id:current.id,
    userId:current.userId,
    status:current.status
  });
}

export function softDeleteSmokingSession(session,{at=new Date().toISOString()}={}){
  const current=createSmokingSession(session);
  if(!['draft','planned','cancelled'].includes(current.status)){
    throw new TypeError('Only an unstarted or cancelled session can be deleted');
  }
  return createSmokingSession({...current,deletedAt:timestamp(at,'at')});
}

export function smokingSessionToRow(session){
  const value=createSmokingSession(session);
  return Object.freeze({
    id:value.id,
    user_id:value.userId,
    status:value.status,
    title:value.title,
    smoker_id:value.smokerId,
    smoker_snapshot:value.smokerSnapshot?smokerSnapshotToJson(value.smokerSnapshot):null,
    recipe_id:value.recipeId,
    recipe_version:value.recipeVersion,
    recipe_source_snapshot:value.recipeSourceSnapshot,
    recipe_use_request_id:value.recipeUseRequestId,
    planner_schedule_snapshot:value.plannerScheduleSnapshot,
    source_session_id:value.sourceSessionId,
    planned_start_at:value.plannedStartAt,
    planned_end_at:value.plannedEndAt,
    actual_start_at:value.actualStartAt,
    actual_end_at:value.actualEndAt,
    active_stage_id:value.activeStageId,
    active_stage_started_at:value.activeStageStartedAt,
    last_stage_mutation_batch_id:value.stageMutationBatchId,
    status_changed_at:value.statusChangedAt,
    deleted_at:value.deletedAt
  });
}

export function smokingSessionFromRow(row={}){
  return createSmokingSession({
    id:row.id,
    userId:row.user_id,
    status:row.status,
    title:row.title,
    smokerId:row.smoker_id,
    smokerSnapshot:row.smoker_snapshot,
    recipeId:row.recipe_id,
    recipeVersion:row.recipe_version,
    recipeSourceSnapshot:row.recipe_source_snapshot,
    recipeUseRequestId:row.recipe_use_request_id,
    plannerScheduleSnapshot:row.planner_schedule_snapshot,
    sourceSessionId:row.source_session_id,
    plannedStartAt:row.planned_start_at,
    plannedEndAt:row.planned_end_at,
    actualStartAt:row.actual_start_at,
    actualEndAt:row.actual_end_at,
    activeStageId:row.active_stage_id,
    activeStageStartedAt:row.active_stage_started_at,
    stageMutationBatchId:row.last_stage_mutation_batch_id,
    statusChangedAt:row.status_changed_at,
    deletedAt:row.deleted_at
  });
}

export function isTerminalSmokingSession(session){
  return TERMINAL.has(createSmokingSession(session).status);
}
